import React, { useEffect } from "react";
import { Button, Popover } from "antd";
import { Icon, IconName } from "../components/ui";
import { ADMIN_CONTACT_NOTE, CLIENT_VERSIONS, COMPANY_NAME, DOWNLOADS, ICP_LINK, ICP_NUMBER } from "../config/navigation";
import "../styles/Home.scss";

/** 账号由团队管理员开通；账号、算力与安装包都请联系管理员 */
const adminCard = (title: string) => (
  <div className="home-support">
    <strong>{title}</strong>
    <p>{ADMIN_CONTACT_NOTE}</p>
  </div>
);

/** 有安装包地址时直接下载，否则提示向团队管理员获取 */
const DownloadButton: React.FC<{ href?: string; label: string; primary?: boolean; icon?: IconName; dark?: boolean }> = ({ href, label, primary, icon = "download", dark }) => {
  const className = `home-btn ${primary ? (dark ? "home-btn--dark" : "home-btn--primary") : "home-btn--secondary"}`;
  if (href) {
    return (
      <a className={className} href={href} download>
        <Icon name={icon} />
        {label}
      </a>
    );
  }
  return (
    <Popover content={adminCard("安装包由团队管理员提供")} trigger={["click"]} placement="top">
      <button type="button" className={className}>
        <Icon name="info" />
        {label}（向管理员获取）
      </button>
    </Popover>
  );
};

const STEPS = [
  { title: "连接店铺", desc: "在桌面端扫码登录拼多多店铺，多个店铺统一管理、随时检验在线状态。" },
  { title: "选择商品", desc: "按商品 ID 批量检索，跨页勾选；只列出在售且有库存的商品。" },
  { title: "配置活动", desc: "选择报名活动与清洗阈值，提交前自动校验会员与算力。" },
  { title: "执行与回看", desc: "逐件执行并实时反馈，进度、失败原因与算力流水都能回看。" },
];

const LEDGER = [
  { title: "算力流水", desc: "充值、赠送、消耗逐笔入账，变动前后余额可以对账。" },
  { title: "任务回看", desc: "每件商品的进度、失败原因与执行时间都会保留。" },
  { title: "权限边界", desc: "管理员、组织管理员与客户各自只看到所需的页面。" },
];

const Home: React.FC = () => {
  useEffect(() => {
    document.title = "多多拼 · 拼多多商家运营工作台";
    return () => {
      document.title = "多多拼 · 运营工作台";
    };
  }, []);

  return (
    <main className="home">
      <section className="home-hero">
        <img className="home-hero__art" src="/illustrations/operations-paper-1600.jpg" alt="纸艺插画：朱红包裹、小票、勾选清单与小店" />
        <header className="home-nav">
          <a className="home-nav__brand" href="/" aria-label="多多拼首页">
            <img src="/brand/duoduopin-mark-256.png" alt="" width={36} height={36} />
            <span>
              <strong>多多拼</strong>
              <small>拼多多商家运营工作台</small>
            </span>
          </a>
          <nav className="home-nav__links" aria-label="首页导航">
            <a href="#flow">业务流程</a>
            <a href="#platforms">三端下载</a>
            <Popover content={adminCard("账号由团队管理员开通")} trigger={["click", "hover"]} placement="bottom">
              <button type="button">如何开通</button>
            </Popover>
          </nav>
          <div className="home-nav__actions">
            <a className="home-btn home-btn--secondary" href="/login">登录</a>
            <a className="home-btn home-btn--primary" href="/login">
              进入工作台
              <Icon name="arrowR" />
            </a>
          </div>
        </header>
        <div className="home-hero__copy">
          <span className="home-kicker home-kicker--chip">为拼多多商家而生的运营系统</span>
          <h1>
            把商品运营，
            <br />
            <em>变成一条清楚的流水线。</em>
          </h1>
          <p>从店铺连接、活动报名，到商品裂变和任务执行，多多拼把分散的日常操作放进同一个可追踪的工作台。</p>
          <div className="home-hero__actions">
            <a className="home-btn home-btn--primary home-btn--lg" href="/login">
              进入工作台
              <Icon name="arrowR" />
            </a>
            <a className="home-btn home-btn--secondary home-btn--lg" href="#platforms">
              <Icon name="download" />
              下载桌面端
            </a>
          </div>
          <div className="home-hero__versions">
            <span><Icon name="grid" size={15} />桌面端 v{CLIENT_VERSIONS.desktop}</span>
            <i aria-hidden="true" />
            <span><Icon name="globe" size={15} />Chrome 插件 v{CLIENT_VERSIONS.extension}</span>
            <i aria-hidden="true" />
            <span><Icon name="users" size={15} />Web 工作台</span>
          </div>
        </div>
      </section>

      <section className="home-steps" id="flow" aria-label="业务流程">
        {STEPS.map((step, index) => (
          <div key={step.title} className="home-steps__item">
            <span className="home-steps__index">0{index + 1}</span>
            <strong>{step.title}</strong>
            <p>{step.desc}</p>
          </div>
        ))}
      </section>

      <section className="home-platforms" id="platforms" aria-labelledby="h-platforms">
        <div className="home-section-head">
          <div>
            <span className="home-kicker">三端协同</span>
            <h2 id="h-platforms">三个入口，一套账号。</h2>
          </div>
          <p>同一个手机号登录三端，任务记录与算力余额在三端之间互通。</p>
        </div>
        <div className="home-platforms__grid">
          <article className="home-platform">
            <div className="home-platform__head">
              <span className="home-platform__icon"><Icon name="grid" size={22} /></span>
              <div><strong>桌面端</strong><small>商家运营工作台</small></div>
            </div>
            <p>连接多个店铺，在本机执行原价卡大促、店铺商品裂变等批量任务。</p>
            <ul>
              <li><Icon name="check" strokeWidth={2} />扫码连接多个拼多多店铺</li>
              <li><Icon name="check" strokeWidth={2} />原价卡大促与 SKU 清洗</li>
              <li><Icon name="check" strokeWidth={2} />商品裂变、标题与主图替换</li>
            </ul>
            <div className="home-platform__actions">
              <DownloadButton href={DOWNLOADS.desktopWin} label="下载 Windows 版" primary />
              {DOWNLOADS.desktopMac && <DownloadButton href={DOWNLOADS.desktopMac} label="macOS 版" />}
            </div>
            <span className="home-platform__meta mono">v{CLIENT_VERSIONS.desktop} · Windows</span>
          </article>
          <article className="home-platform">
            <div className="home-platform__head">
              <span className="home-platform__icon"><Icon name="globe" size={22} /></span>
              <div><strong>Chrome 插件</strong><small>商家伴侣</small></div>
            </div>
            <p>在拼多多商家后台的商品列表页，直接选择商品发起防比价报名。</p>
            <ul>
              <li><Icon name="check" strokeWidth={2} />商品列表页内直接操作</li>
              <li><Icon name="check" strokeWidth={2} />批量报名与阈值清洗</li>
              <li><Icon name="check" strokeWidth={2} />侧边栏查看任务与算力</li>
            </ul>
            <div className="home-platform__actions">
              <DownloadButton href={DOWNLOADS.extension} label="下载插件包（.zip）" primary />
            </div>
            <span className="home-platform__meta mono">v{CLIENT_VERSIONS.extension} · 解压后开发者模式加载</span>
          </article>
          <article className="home-platform">
            <div className="home-platform__head">
              <span className="home-platform__icon"><Icon name="users" size={22} /></span>
              <div><strong>Web 工作台</strong><small>团队与账务</small></div>
            </div>
            <p>管理团队账号、组织与算力定价，回看每一次任务与登录。</p>
            <ul>
              <li><Icon name="check" strokeWidth={2} />账号、角色与邀请码</li>
              <li><Icon name="check" strokeWidth={2} />算力流水与定价配置</li>
              <li><Icon name="check" strokeWidth={2} />登录审计与会话管理</li>
            </ul>
            <div className="home-platform__actions">
              <a className="home-btn home-btn--dark" href="/login">
                进入工作台
                <Icon name="arrowR" />
              </a>
            </div>
            <span className="home-platform__meta mono">浏览器访问，无需安装</span>
          </article>
        </div>
      </section>

      <section className="home-ledger" aria-labelledby="h-ledger">
        <div className="home-ledger__head">
          <span className="home-kicker home-kicker--on-ink">可追踪</span>
          <h2 id="h-ledger">
            每一次消耗，
            <br />
            都有记录。
          </h2>
        </div>
        <div className="home-ledger__grid">
          {LEDGER.map((item, index) => (
            <div key={item.title}>
              <span className="mono">0{index + 1}</span>
              <strong>{item.title}</strong>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="home-cta">
        <img src="/brand/duoduopin-mark-256.png" alt="" width={56} height={56} />
        <h2>
          先把今天最重要的任务，
          <br />
          放进多多拼。
        </h2>
        <div className="home-cta__actions">
          <a className="home-btn home-btn--primary home-btn--lg" href="/login">
            进入工作台
            <Icon name="arrowR" />
          </a>
          <Popover content={adminCard("账号由团队管理员开通")} trigger={["click"]} placement="top">
            <Button size="large" className="home-btn home-btn--secondary home-btn--lg" icon={<Icon name="info" />}>
              如何开通账号
            </Button>
          </Popover>
        </div>
      </section>

      <footer className="home-footer">
        <div className="home-footer__legal">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}</span>
          <a href="/user-privacy-agreement">用户协议与隐私说明</a>
          <a href={ICP_LINK} target="_blank" rel="noreferrer">{ICP_NUMBER}</a>
        </div>
        <div className="home-footer__brand">
          <img src="/brand/duoduopin-mark-256.png" alt="" width={20} height={20} />
          <span>多多拼</span>
        </div>
      </footer>
    </main>
  );
};

export default Home;
